import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, useAuth } from "../App";
import { useHygienaEnabled } from "../lib/hygienaStatus";
import { HygienaSwitchCard } from "./HygienaSwitchCard";
import {
  Sparkles, KeyRound, ShieldAlert, Search, Clock, Loader2,
  CheckCircle, AlertTriangle,
} from "lucide-react";

/**
 * Hygiena AI Admin panel — admin-only surface inside AdminPage. Three core
 * tools: password reset, content moderation scan, member credential
 * cross-reference. Plus a recent-actions audit list at the bottom.
 */
const HygienaTools = () => {
  const [resetEmail, setResetEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  const [scanWindow, setScanWindow] = useState(24);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [xrefUserId, setXrefUserId] = useState("");
  const [xrefBusy, setXrefBusy] = useState(false);
  const [xrefResult, setXrefResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/hygiena/moderation-history?limit=25`);
      setHistory(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch {
      // silent; banner shown elsewhere if needed
    }
  };

  const handleReset = async () => {
    if (!resetEmail.trim()) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/hygiena/reset-member-password`, {
        email: resetEmail.trim().toLowerCase(),
      });
      toast.success(
        res.data.email_sent
          ? "Temp password generated and emailed to the member."
          : "Temp password generated, but the email send returned false (check SendGrid DKIM/SPF)."
      );
      setResetEmail("");
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/admin/hygiena/scan-content`, {
        hours_back: scanWindow,
        sample_size: 50,
      });
      setScanResult(res.data);
      toast.success(`Scanned ${res.data.scanned} items, hid ${res.data.flagged_count}.`);
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleXref = async () => {
    if (!xrefUserId.trim()) return;
    setXrefBusy(true);
    setXrefResult(null);
    try {
      const res = await axios.post(`${API}/admin/hygiena/cross-reference`, {
        user_id: xrefUserId.trim(),
      });
      setXrefResult(res.data);
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cross-reference failed");
    } finally {
      setXrefBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="hygiena-admin-panel">
      <div className="bg-gradient-to-br from-[#F0ECF7] to-[#FAF7F2] border border-[#E5DFEC] rounded-2xl p-5 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#6B4E8A] flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-base font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Hygiena AI Admin
          </p>
          <p className="text-xs text-[#64748B] leading-relaxed mt-1">
            Admin-only AI tools. Members never see Hygiena. Use it to issue temp passwords, run a moderation sweep, or sanity-check member credentials before approval. Every action is logged in the admin audit trail.
          </p>
        </div>
      </div>

      {/* 1. Password Reset */}
      <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid="hygiena-pw-reset-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-[#6B4E8A]" />
          <h3 className="text-sm font-medium text-[#6B4E8A]">Password reset</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Hygiena generates a 14-character temp password, hashes it, flags the account to require a change on next login, and emails the member the password directly.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="member@example.com"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="flex-1"
            data-testid="hygiena-reset-email-input"
          />
          <Button
            onClick={handleReset}
            disabled={resetting || !resetEmail.trim()}
            className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-5 shrink-0"
            data-testid="hygiena-reset-submit"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Issue temp password"}
          </Button>
        </div>
      </div>

      {/* 2. Content scan */}
      <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid="hygiena-scan-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-4 h-4 text-[#6B4E8A]" />
          <h3 className="text-sm font-medium text-[#6B4E8A]">Content moderation scan</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Sweeps recent discussions, replies, social comments, and clinic reviews. Anything flagged for hate / harassment / personal attacks / graphic content / spam is auto-hidden and the author is emailed a warning.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <label className="text-xs text-[#475569] inline-flex items-center gap-2">
            Look back
            <select
              value={scanWindow}
              onChange={(e) => setScanWindow(Number(e.target.value))}
              className="border border-[#E5DFEC] rounded-md px-2 py-1 bg-white text-xs"
              data-testid="hygiena-scan-window"
            >
              <option value={6}>6 hours</option>
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
            </select>
          </label>
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="bg-[#8DB5A0] text-white hover:bg-[#7A9F8C] rounded-full px-5"
            data-testid="hygiena-scan-submit"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run scan now"}
          </Button>
        </div>
        {scanResult && (
          <div className="mt-4 text-xs space-y-1" data-testid="hygiena-scan-result">
            <div className="text-[#6B4E8A]">
              Scanned <strong>{scanResult.scanned}</strong> items in the last {scanResult.window_hours}h. Hid <strong>{scanResult.flagged_count}</strong>.
            </div>
            {scanResult.flagged_count > 0 && (
              <ul className="space-y-1 mt-2">
                {scanResult.flagged.slice(0, 5).map((f, i) => (
                  <li key={`${f.source}-${f.id || i}`} className="text-[#64748B] flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-[#EF4444] mt-0.5 shrink-0" />
                    <span><strong>{f.source}</strong> · {f.category} · {f.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* 3. Cross-reference */}
      <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid="hygiena-xref-card">
        <div className="flex items-center gap-2 mb-1">
          <Search className="w-4 h-4 text-[#6B4E8A]" />
          <h3 className="text-sm font-medium text-[#6B4E8A]">Member credential cross-reference</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Plausibility check on a member's submitted province, registration number, and uploaded documents. Hygiena maps the province to the right regulator and flags format mismatches. Final decision stays with you — this is non-binding.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="user_id (e.g. user_xxxxxx)"
            value={xrefUserId}
            onChange={(e) => setXrefUserId(e.target.value)}
            className="flex-1 font-mono text-xs"
            data-testid="hygiena-xref-userid-input"
          />
          <Button
            onClick={handleXref}
            disabled={xrefBusy || !xrefUserId.trim()}
            variant="outline"
            className="border-[#6B4E8A] text-[#6B4E8A] hover:bg-[#F0ECF7] rounded-full px-5 shrink-0"
            data-testid="hygiena-xref-submit"
          >
            {xrefBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cross-reference"}
          </Button>
        </div>
        {xrefResult && (
          <div className="mt-4 bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg p-3 text-xs space-y-2" data-testid="hygiena-xref-result">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-[#94A3B8]">Member:</span> <strong className="text-[#6B4E8A]">{xrefResult.user.name}</strong></div>
              <div><span className="text-[#94A3B8]">Province:</span> {xrefResult.user.province || "—"}</div>
              <div><span className="text-[#94A3B8]">Reg #:</span> {xrefResult.user.registration_number}</div>
              <div><span className="text-[#94A3B8]">Status:</span> {xrefResult.user.verification_status}</div>
              <div><span className="text-[#94A3B8]">ID doc:</span> {xrefResult.user.has_id_doc ? "✓" : "—"}</div>
              <div><span className="text-[#94A3B8]">Permit:</span> {xrefResult.user.has_permit ? "✓" : "—"}</div>
            </div>
            <div className="pt-2 border-t border-[#E5DFEC]">
              <p className="text-[#6B4E8A]"><strong>Hygiena verdict:</strong> {xrefResult.verdict.verdict} · {xrefResult.verdict.regulator}</p>
              <p className="text-[#475569] mt-1">{xrefResult.verdict.recommendation}</p>
            </div>
          </div>
        )}
      </div>

      {/* 4. Recent actions */}
      <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid="hygiena-history-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#6B4E8A]" />
            <h3 className="text-sm font-medium text-[#6B4E8A]">Recent Hygiena actions</h3>
          </div>
          <button onClick={fetchHistory} className="text-xs text-[#94A3B8] hover:text-[#6B4E8A]">Refresh</button>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">No Hygiena actions yet.</p>
        ) : (
          <ul className="space-y-2">
            {history.map((row) => (
              <li
                key={row.audit_id || `${row.created_at}-${row.action}`}
                className="text-xs border-b border-[#E5DFEC] pb-2 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-[#10B981] shrink-0" />
                  <span className="text-[#6B4E8A] font-medium">{(row.action || "").replace("hygiena_", "")}</span>
                  <span className="text-[#94A3B8]">· {row.admin_email}</span>
                  <span className="text-[#94A3B8] ml-auto">{new Date(row.created_at).toLocaleString("en-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
                {row.details && (
                  <p className="text-[#64748B] mt-1 pl-5">{row.details}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const HygienaAdminPanel = () => {
  const { user } = useAuth();
  const enabled = useHygienaEnabled(true);
  const founder = user?.email === "ramneetrdh@gmail.com";
  if (enabled === null) return null;
  return (
    <div className="pb-28">
      {founder && <HygienaSwitchCard enabled={enabled} />}
      {enabled ? <HygienaTools /> : (
        <p className="text-sm text-[#64748B] bg-white border border-[#E5DFEC] rounded-2xl p-5" data-testid="hygiena-unavailable">Hygiena is unavailable until further notice.</p>
      )}
    </div>
  );
};

export default HygienaAdminPanel;
